import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "./Modal";
import api from "../../services/api";
import { sendToMobileApp } from "../../utils/utils";
import { normalizeMicCheckResult, normalizeMicCheckStopped, registerMicCheckOperation } from "../../utils/micCheckCoordinator";

const SPEED_TEST_URL = "https://speed.cloudflare.com/__down";
const WARMUP_REQUESTS = 3;
const LATENCY_SAMPLES = 6;
const DOWNLOAD_CHUNKS = 4;
const DOWNLOAD_CHUNK_SIZE = 2000000;
const REQUEST_TIMEOUT_MS = 10000;
const GOOD_SPEED_MBPS = 5;
const MIN_SPEED_MBPS = 2;
const GOOD_LATENCY_MS = 100;
const MIN_LATENCY_MS = 300;
const MIC_TEST_DURATION_MS = 5000;
const PERMISSION_QUERY_TIMEOUT_MS = 3000;
const MIC_ACQUISITION_TIMEOUT_MS = 15000;
const NATIVE_MIC_RESULT_TIMEOUT_MS = 15000;
const AUDIO_THRESHOLD = 20;
const toneMap = { ok: "success", warning: "warning", error: "danger", running: "info", pending: "muted" };
const getTone = (status) => toneMap[status] || "muted";
const stopStream = (stream) => stream?.getTracks?.().forEach((track) => track.stop());
const formatSpeed = (mbps) => (mbps != null ? mbps.toFixed(1) : "--");
const formatLatency = (ms) => (ms != null ? Math.round(ms) : "--");
const trimmedMean = (values, trimPercent = 0.2) => {
  if (values.length < 3) return values.reduce((sum, value) => sum + value, 0) / values.length;
  const sorted = [...values].sort((a, b) => a - b);
  const trimCount = Math.floor(sorted.length * trimPercent);
  const trimmed = sorted.slice(trimCount, sorted.length - trimCount);
  return trimmed.reduce((sum, value) => sum + value, 0) / trimmed.length;
};
let requestSequence = 0;
const createRequestId = () => {
  const uuid = typeof crypto !== "undefined" ? crypto.randomUUID?.() : undefined;
  if (uuid) return `health-check-${uuid}`;
  requestSequence += 1;
  return `health-check-${Date.now()}-${requestSequence}-${Math.random().toString(36).slice(2)}`;
};

const summaryKeys = {
  success: { title: "healthCheck.summary.allReady", subtitle: "healthCheck.summary.readyToRecord" },
  warning: { title: "healthCheck.summary.someIssues", subtitle: "healthCheck.summary.reviewIssues" },
  danger: { title: "healthCheck.summary.criticalIssues", subtitle: "healthCheck.summary.fixIssues" },
  info: { title: "healthCheck.summary.running", subtitle: "healthCheck.summary.moment" },
  muted: { title: "healthCheck.summary.title", subtitle: "healthCheck.summary.subtitle" },
};

// Product names we can actually source. A recognized white-label with no
// verified name falls back to a neutral label — never to "Lucas", which is what
// leaked the parent brand into white-label portals. Add a line here to name one.
const SERVICE_BRAND_NAMES = {
  // Unbranded/default deployments are Lucas; "lucas" is also accepted so an
  // explicit main-app brand resolves the same way.
  lucas: "Lucas",
  rpg: "Compass AI",
  freehealthai: "FreeHealth.ai",
};

const FixList = ({ items }) => (
  <ol className="fix-list">
    {items.map((item, index) => (
      <li key={`${index}-${item}`}>{item}</li>
    ))}
  </ol>
);

// Hoisted out of the component body: redefining these on every render gives them
// a new component identity, so React remounts each card's DOM on every progress
// tick and native volume event (dropping focus from the Test buttons).
const Card = ({ icon, label, result, onTest, description, testLabel, disabled, children }) => {
  const tone = getTone(result.status);
  return (
    <div className={`health-card ${tone}`}>
      <div className="card-header">
        <div className="card-title">
          <span className={`status-dot ${tone}`} />
          <div>
            <div className="label">{label}</div>
            <div className="description">{description}</div>
          </div>
        </div>
        <button className="ghost" onClick={onTest} disabled={disabled}>
          <i className="material-symbols-outlined">{icon}</i>
          {testLabel}
        </button>
      </div>
      {children}
    </div>
  );
};

function ModalHealthCheck({ show, close, brand }) {
  const { t } = useTranslation();
  const [speedTest, setSpeedTest] = useState({
    status: "pending",
    downloadMbps: null,
    latencyMs: null,
    message: "",
    progress: 0,
  });
  const [micTest, setMicTest] = useState({ status: "pending", message: "", volumeLevel: 0, audioDetected: false });
  const [serverStatus, setServerStatus] = useState({ status: "pending", message: "" });
  const [isRunningAll, setIsRunningAll] = useState(false);
  const mountedRef = useRef(true);
  const shownRef = useRef(show);
  const runNumberRef = useRef(0);
  const activeRunRef = useRef(null);
  const micStreamRef = useRef(null);
  const audioContextRef = useRef(null);
  const audioSourceRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const micTimerRef = useRef(null);
  const isMobileApp = typeof window !== "undefined" && !!window.ReactNativeWebView;
  const isRpg = brand === "rpg";
  const defersNativeMicCheck = isRpg && isMobileApp;

  const isCurrent = useCallback(
    (run) => mountedRef.current && shownRef.current && activeRunRef.current === run && !run.cancelled,
    []
  );
  const safeSet = useCallback((run, setter) => isCurrent(run) && setter(), [isCurrent]);

  const cleanupMic = useCallback(
    (run, stopNative = false) => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      if (micTimerRef.current !== null) clearTimeout(micTimerRef.current);
      if (run?.sampleTimer !== undefined) clearTimeout(run.sampleTimer);
      if (run?.nativeTimer !== undefined) clearTimeout(run.nativeTimer);
      audioSourceRef.current?.disconnect?.();
      audioContextRef.current?.close?.().catch(() => {});
      stopStream(micStreamRef.current);
      rafRef.current = micTimerRef.current = null;
      micStreamRef.current = audioContextRef.current = audioSourceRef.current = analyserRef.current = null;
      if (stopNative && isMobileApp && run?.nativeRequestId && !run.nativeStopSent) {
        run.nativeStopSent = true;
        sendToMobileApp({ type: "stopMicCheck", requestId: run.nativeRequestId });
      }
    },
    [isMobileApp]
  );

  const cancelActiveRun = useCallback(() => {
    const run = activeRunRef.current;
    if (!run) return;
    run.cancelled = true;
    // Establish shared legacy ambiguity before clearing local pending state.
    if (run.nativePending) run.nativeOperation?.cancel();
    run.nativePending = false;
    run.controller.abort();
    cleanupMic(run, true);
    activeRunRef.current = null;
  }, [cleanupMic]);

  const startRun = useCallback(() => {
    cancelActiveRun();
    const run = { id: ++runNumberRef.current, controller: new AbortController(), cancelled: false };
    activeRunRef.current = run;
    return run;
  }, [cancelActiveRun]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      cancelActiveRun();
    };
  }, [cancelActiveRun]);

  const fetchWithTimeout = async (url, options, timeout = REQUEST_TIMEOUT_MS) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeout);
    const abortParent = () => controller.abort();
    options.signal?.addEventListener?.("abort", abortParent, { once: true });
    try {
      return await fetch(url, { ...options, signal: controller.signal });
    } finally {
      clearTimeout(timer);
      options.signal?.removeEventListener?.("abort", abortParent);
    }
  };

  const settleForRun = (promise, run, timeoutMs) =>
    new Promise((resolve, reject) => {
      let settled = false;
      const finish = (callback, value) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        run.controller.signal.removeEventListener("abort", onAbort);
        callback(value);
      };
      const onAbort = () => finish(reject, Object.assign(new Error("cancelled"), { name: "AbortError" }));
      const timer = setTimeout(() => finish(reject, Object.assign(new Error("timed out"), { name: "TimeoutError" })), timeoutMs);
      run.controller.signal.addEventListener("abort", onAbort, { once: true });
      Promise.resolve(promise).then(
        (value) => finish(resolve, value),
        (error) => finish(reject, error)
      );
    });

  const runSpeedTest = async (run) => {
    const updateProgress = (progress, message) => {
      safeSet(run, () => setSpeedTest((previous) => ({ ...previous, progress, message })));
    };
    safeSet(run, () =>
      setSpeedTest({
        status: "running",
        downloadMbps: null,
        latencyMs: null,
        message: t("healthCheck.internet.warmingUp"),
        progress: 0,
      })
    );
    try {
      for (let index = 0; index < WARMUP_REQUESTS; index += 1) {
        if (!isCurrent(run)) return null;
        await fetchWithTimeout(
          `${SPEED_TEST_URL}?bytes=1000&t=${Date.now()}-${index}`,
          { cache: "no-store", signal: run.controller.signal },
          5000
        );
        updateProgress(Math.round(((index + 1) / WARMUP_REQUESTS) * 15), t("healthCheck.internet.warmingUp"));
      }

      updateProgress(15, t("healthCheck.internet.measuringLatency"));
      const latencies = [];
      for (let index = 0; index < LATENCY_SAMPLES; index += 1) {
        if (!isCurrent(run)) return null;
        const started = performance.now();
        await fetchWithTimeout(
          `${SPEED_TEST_URL}?bytes=1&t=${Date.now()}-${index}`,
          { cache: "no-store", signal: run.controller.signal },
          3000
        );
        latencies.push(performance.now() - started);
        updateProgress(15 + Math.round(((index + 1) / LATENCY_SAMPLES) * 25), t("healthCheck.internet.measuringLatency"));
      }
      const latencyMs = trimmedMean(latencies);

      updateProgress(40, t("healthCheck.internet.testingDownload"));
      const speeds = [];
      for (let index = 0; index < DOWNLOAD_CHUNKS; index += 1) {
        if (!isCurrent(run)) return null;
        const started = performance.now();
        const response = await fetchWithTimeout(
          `${SPEED_TEST_URL}?bytes=${DOWNLOAD_CHUNK_SIZE}&t=${Date.now()}-${index}`,
          { cache: "no-store", signal: run.controller.signal },
          REQUEST_TIMEOUT_MS
        );
        const buffer = await response.arrayBuffer();
        const seconds = Math.max((performance.now() - started) / 1000, 0.001);
        speeds.push((buffer.byteLength * 8) / seconds / 1_000_000);
        updateProgress(40 + Math.round(((index + 1) / DOWNLOAD_CHUNKS) * 60), t("healthCheck.internet.testingDownload"));
      }
      if (!isCurrent(run)) return null;
      const downloadMbps = trimmedMean(speeds);
      let status = "error";
      if (downloadMbps >= GOOD_SPEED_MBPS && latencyMs < GOOD_LATENCY_MS) status = "ok";
      else if (downloadMbps >= MIN_SPEED_MBPS && latencyMs < MIN_LATENCY_MS) status = "warning";
      safeSet(run, () =>
        setSpeedTest({
          status,
          downloadMbps,
          latencyMs,
          message: status === "error" ? t("healthCheck.internet.belowRecommended") : "",
          progress: 100,
        })
      );
      return { downloadMbps, latencyMs };
    } catch (error) {
      if (!isCurrent(run)) return null;
      safeSet(run, () =>
        setSpeedTest({
          status: "error",
          downloadMbps: null,
          latencyMs: null,
          message: error.name === "AbortError" ? t("healthCheck.internet.timedOut") : t("healthCheck.internet.failed"),
          progress: 0,
        })
      );
      return null;
    }
  };

  const errorReason = (error) => {
    if (["NotAllowedError", "PermissionDeniedError", "SecurityError"].includes(error?.name)) return ["error", "accessDenied"];
    if (error?.name === "NotFoundError") return ["error", "noDevice"];
    if (["NotReadableError", "AbortError"].includes(error?.name)) return ["warning", "unavailable"];
    return ["error", "notSupported"];
  };

  const checkMicrophoneWeb = async (run) => {
    cleanupMic(run);
    safeSet(run, () =>
      setMicTest({ status: "running", message: t("healthCheck.mic.checking"), volumeLevel: 0, audioDetected: false })
    );
    if (!navigator.mediaDevices?.getUserMedia) {
      safeSet(run, () =>
        setMicTest({ status: "error", message: t("healthCheck.mic.notSupported"), volumeLevel: 0, audioDetected: false })
      );
      return null;
    }
    try {
      try {
        const query = navigator.permissions?.query?.({ name: "microphone" });
        const permission = query ? await settleForRun(query, run, PERMISSION_QUERY_TIMEOUT_MS) : undefined;
        if (!isCurrent(run)) return null;
        if (permission?.state === "denied") {
          safeSet(run, () =>
            setMicTest({ status: "error", message: t("healthCheck.mic.accessDenied"), volumeLevel: 0, audioDetected: false })
          );
          return null;
        }
      } catch (error) {
        if (error?.name === "AbortError") return null;
        // Permission API support is optional (notably on Safari). Query errors
        // and timeouts are inconclusive, so continue to the real media request.
      }
      if (!isCurrent(run)) return null;
      const stream = await new Promise((resolve, reject) => {
        let settled = false;
        const acquisitionTimer = setTimeout(() => {
          settled = true;
          reject(Object.assign(new Error("timeout"), { name: "TimeoutError" }));
        }, MIC_ACQUISITION_TIMEOUT_MS);
        navigator.mediaDevices.getUserMedia({ audio: true }).then(
          (value) => {
            clearTimeout(acquisitionTimer);
            if (settled || !isCurrent(run)) {
              stopStream(value);
              return;
            }
            settled = true;
            resolve(value);
          },
          (error) => {
            clearTimeout(acquisitionTimer);
            if (settled) return;
            settled = true;
            reject(error);
          }
        );
      });
      if (!stream || !isCurrent(run)) return null;
      micStreamRef.current = stream;
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      audioSourceRef.current = audioContextRef.current.createMediaStreamSource(stream);
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;
      audioSourceRef.current.connect(analyserRef.current);
      safeSet(run, () => setMicTest((previous) => ({ ...previous, message: t("healthCheck.mic.speakToTest") })));
      let detected = false;
      const values = new Uint8Array(analyserRef.current.frequencyBinCount);
      const analyse = () => {
        if (!isCurrent(run) || !analyserRef.current) return;
        analyserRef.current.getByteFrequencyData(values);
        const volumeLevel = Math.min(100, (values.reduce((sum, value) => sum + value, 0) / values.length / 128) * 100);
        detected ||= volumeLevel > AUDIO_THRESHOLD;
        safeSet(run, () =>
          setMicTest((previous) => ({ ...previous, volumeLevel, audioDetected: previous.audioDetected || detected }))
        );
        rafRef.current = requestAnimationFrame(analyse);
      };
      analyse();
      return await new Promise((resolve) => {
        micTimerRef.current = setTimeout(() => {
          if (!isCurrent(run)) return resolve(null);
          cleanupMic(run);
          safeSet(run, () =>
            setMicTest({
              status: detected ? "ok" : "warning",
              message: t(detected ? "healthCheck.mic.working" : "healthCheck.mic.noAudio"),
              volumeLevel: 0,
              audioDetected: detected,
            })
          );
          resolve({ detected });
        }, MIC_TEST_DURATION_MS);
      });
    } catch (error) {
      if (!isCurrent(run)) return null;
      cleanupMic(run);
      const [status, reason] = error?.name === "TimeoutError" ? ["warning", "timedOut"] : errorReason(error);
      safeSet(run, () => setMicTest({ status, message: t(`healthCheck.mic.${reason}`), volumeLevel: 0, audioDetected: false }));
      return null;
    }
  };

  const checkMicrophoneNative = (run) => {
    cleanupMic(run);
    run.nativeRequestId = createRequestId();
    run.nativePending = true;
    safeSet(run, () =>
      setMicTest({ status: "running", message: t("healthCheck.mic.checking"), volumeLevel: 0, audioDetected: false })
    );
    return new Promise((resolve) => {
      const finish = (status, reason, audioDetected = false) => {
        if (!isCurrent(run)) return resolve(null);
        run.nativePending = false;
        run.nativeOperation?.complete();
        cleanupMic(run);
        safeSet(run, () => setMicTest({ status, message: t(`healthCheck.mic.${reason}`), volumeLevel: 0, audioDetected }));
        resolve({ status, reason });
      };
      const onVolume = (detail) => {
        if (!isCurrent(run)) return;
        const volumeLevel = Number(detail?.volumeLevel) || 0;
        safeSet(run, () =>
          setMicTest((previous) => ({
            ...previous,
            message: t("healthCheck.mic.speakToTest"),
            volumeLevel,
            audioDetected: previous.audioDetected || volumeLevel > AUDIO_THRESHOLD,
          }))
        );
      };
      const onResult = (detail) => {
        const result = normalizeMicCheckResult(detail);
        finish(result.status, result.reason, result.audioDetected);
      };
      const onStopped = (detail) => {
        const result = normalizeMicCheckStopped(detail);
        finish(result.status, result.reason, result.audioDetected);
      };
      run.nativeOperation = registerMicCheckOperation({ requestId: run.nativeRequestId, onVolume, onResult, onStopped });
      run.nativeTimer = setTimeout(() => {
        if (!isCurrent(run)) return resolve(null);
        // Enter the shared drain before cleanup makes this operation inactive.
        run.nativeOperation?.cancel();
        cleanupMic(run, true);
        run.nativePending = false;
        safeSet(run, () =>
          setMicTest({ status: "warning", message: t("healthCheck.mic.timedOut"), volumeLevel: 0, audioDetected: false })
        );
        resolve({ status: "warning", reason: "timedOut" });
      }, NATIVE_MIC_RESULT_TIMEOUT_MS);
      sendToMobileApp({ type: "checkMicrophone", requestId: run.nativeRequestId });
    });
  };

  const checkMicrophone = (run) => {
    if (defersNativeMicCheck) {
      cleanupMic(run, true);
      // Not a fault: nothing has been tested yet. A "warning" here would pin the
      // overall banner to "Some Issues Detected / review issues below" for every
      // healthy Compass device — with no issues listed, because the fix list is
      // suppressed on this path.
      safeSet(run, () =>
        setMicTest({
          status: "pending",
          message: t("healthCheck.mic.checkedOnRecord"),
          volumeLevel: 0,
          audioDetected: false,
        })
      );
      return Promise.resolve({ status: "pending", reason: "deferred_to_recording" });
    }
    return isMobileApp ? checkMicrophoneNative(run) : checkMicrophoneWeb(run);
  };

  const checkServer = async (run) => {
    safeSet(run, () => setServerStatus({ status: "running", message: t("healthCheck.services.checking") }));
    try {
      const result = await settleForRun(api.healthCheck(), run, REQUEST_TIMEOUT_MS);
      safeSet(run, () =>
        setServerStatus({
          status: "ok",
          message: typeof result?.data === "string" ? result.data : result?.data?.status || t("healthCheck.services.connected"),
        })
      );
    } catch (error) {
      // Keep the HTTP status when there is one — "HTTP 502" is the difference
      // between a support call that lands and one that guesses.
      const code = error?.cause?.response?.status;
      safeSet(run, () =>
        setServerStatus({ status: "error", message: code ? `HTTP ${code}` : t("healthCheck.services.unreachable") })
      );
    }
  };

  const runAllChecks = useCallback(async () => {
    const run = startRun();
    safeSet(run, () => setIsRunningAll(true));
    try {
      await Promise.all([runSpeedTest(run), checkMicrophone(run), checkServer(run)]);
    } finally {
      safeSet(run, () => setIsRunningAll(false));
    }
    // The helpers intentionally read the latest translation and platform state on a new modal run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startRun, safeSet, isMobileApp, defersNativeMicCheck]);

  useEffect(() => {
    shownRef.current = show;
    if (show) runAllChecks();
    else cancelActiveRun();
  }, [show, runAllChecks, cancelActiveRun]);

  const runOne = (checker) => {
    const run = startRun();
    return checker(run);
  };
  const isBusy = isRunningAll || [speedTest, micTest, serverStatus].some((result) => result.status === "running");
  const overall = useMemo(() => {
    // A deferred mic check is not a result, so it must not drag the roll-up down.
    // The banner then reports what was actually tested; the mic card says on its
    // own that permission is checked when recording starts.
    const statuses = [speedTest.status, serverStatus.status, ...(defersNativeMicCheck ? [] : [micTest.status])];
    if (statuses.includes("error")) return "danger";
    if (statuses.includes("warning")) return "warning";
    if (statuses.every((status) => status === "ok")) return "success";
    return statuses.includes("running") ? "info" : "muted";
  }, [speedTest, micTest, serverStatus, defersNativeMicCheck]);
  const summary = summaryKeys[overall];
  const serviceBrandName = SERVICE_BRAND_NAMES[brand || "lucas"];
  const serviceLabel = serviceBrandName
    ? t("healthCheck.services.label", { brand: serviceBrandName })
    : t("healthCheck.services.labelGeneric");
  const describe = (result) => (result.status === "pending" ? t("healthCheck.notTested") : result.message);
  const speedDescription =
    speedTest.status === "running"
      ? speedTest.message
      : speedTest.status === "ok" || speedTest.status === "warning"
        ? `${formatSpeed(speedTest.downloadMbps)} Mbps, ${formatLatency(speedTest.latencyMs)}ms`
        : speedTest.status === "error"
          ? speedTest.message || t("healthCheck.internet.connectionIssue")
          : t("healthCheck.notTested");

  return (
    <Modal
      show={show}
      close={() => {
        cancelActiveRun();
        close();
      }}
      className={`modal-health-check${isRpg ? " brand-rpg" : ""} status-${overall}`}
      classNameWrapper={isRpg ? "brand-rpg" : ""}
      size="small"
      hideTitle
      hideClose
      action={
        <>
          <button
            className="white"
            onClick={() => {
              cancelActiveRun();
              close();
            }}
          >
            {t("healthCheck.close")}
          </button>
          <button onClick={runAllChecks} disabled={isBusy}>
            {isBusy ? t("healthCheck.runningChecks") : t("healthCheck.recheckSystem")}
          </button>
        </>
      }
    >
      <div className="health-check-body">
        <div className={`status-banner ${overall}`}>
          <div className="status-icon">
            <i className="notranslate material-symbols-outlined">
              {overall === "success"
                ? "check_circle"
                : overall === "danger"
                  ? "highlight_off"
                  : overall === "warning"
                    ? "error"
                    : "health_and_safety"}
            </i>
          </div>
          <div className="status-text">
            <div className="status-title">{t(summary.title)}</div>
            <div className="status-subtitle">{t(summary.subtitle)}</div>
          </div>
        </div>
        <div className="checks-grid">
          <Card
            icon="speed"
            label={t("healthCheck.internet.label")}
            result={speedTest}
            description={speedDescription}
            testLabel={t("healthCheck.test")}
            disabled={isBusy || speedTest.status === "running"}
            onTest={() => runOne(runSpeedTest)}
          >
            {speedTest.status === "running" && (
              <div className="speed-progress">
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${speedTest.progress || 15}%` }} />
                </div>
              </div>
            )}
            {speedTest.status !== "running" && speedTest.status !== "pending" && (
              <div className="metric-grid">
                <div className="metric">
                  <div className="metric-value">{formatSpeed(speedTest.downloadMbps)} Mbps</div>
                  <div className="metric-label">{t("healthCheck.internet.download")}</div>
                </div>
                <div className="metric">
                  <div className="metric-value">{formatLatency(speedTest.latencyMs)} ms</div>
                  <div className="metric-label">{t("healthCheck.internet.latency")}</div>
                </div>
              </div>
            )}
            {(speedTest.status === "warning" || speedTest.status === "error") && (
              <FixList
                items={[
                  t("healthCheck.internet.fix.wifi"),
                  t("healthCheck.internet.fix.router"),
                  t("healthCheck.internet.fix.apps"),
                  t("healthCheck.internet.fix.restart"),
                ]}
              />
            )}
          </Card>
          <Card
            icon="mic"
            label={t("healthCheck.mic.label")}
            result={micTest}
            description={defersNativeMicCheck ? t("healthCheck.mic.checkedOnRecord") : describe(micTest)}
            testLabel={t("healthCheck.test")}
            disabled={isBusy || micTest.status === "running"}
            onTest={() => runOne(checkMicrophone)}
          >
            {micTest.status === "running" && (
              <div className="volume-meter">
                <div className="volume-bars">
                  {Array.from({ length: 10 }, (_, index) => (
                    <div key={index} className={`volume-bar ${index < Math.round(micTest.volumeLevel / 10) ? "active" : ""}`} />
                  ))}
                </div>
              </div>
            )}
            {!defersNativeMicCheck && (micTest.status === "warning" || micTest.status === "error") && (
              <FixList
                items={
                  isMobileApp
                    ? [
                        t("healthCheck.mic.fix.mobileSettings"),
                        t("healthCheck.mic.fix.mobileOtherApp"),
                        t("healthCheck.mic.fix.mobileRestart"),
                      ]
                    : [
                        t("healthCheck.mic.fix.lockIcon"),
                        t("healthCheck.mic.fix.allowPermissions"),
                        t("healthCheck.mic.fix.pluggedIn"),
                        t("healthCheck.mic.fix.refreshPage"),
                      ]
                }
              />
            )}
          </Card>
          <Card
            icon="cloud_done"
            label={serviceLabel}
            result={serverStatus}
            description={describe(serverStatus)}
            testLabel={t("healthCheck.test")}
            disabled={isBusy || serverStatus.status === "running"}
            onTest={() => runOne(checkServer)}
          >
            {(serverStatus.status === "warning" || serverStatus.status === "error") && (
              <FixList
                items={[
                  t("healthCheck.services.fix.verifyNetwork"),
                  t("healthCheck.services.fix.logOut"),
                  t("healthCheck.services.fix.contactIT"),
                ]}
              />
            )}
          </Card>
        </div>
      </div>
    </Modal>
  );
}

export default ModalHealthCheck;
